import { appErrorMonitorService, AppErrorLevel } from './appErrorMonitorService';
import { telemetryMonitorService } from './telemetryMonitorService';

const MAX_EVENTS_PER_SESSION = 200;
const MAX_MESSAGE_LENGTH = 2000;
const MAX_CONTEXT_LENGTH = 4000;
const MAX_ROUTE_CONTEXT_DEPTH = 3;
const MAX_ROUTE_ARRAY_ITEMS = 12;
const MAX_ROUTE_OBJECT_KEYS = 30;

const SENSITIVE_CONTEXT_KEYWORDS = [
  'password',
  'token',
  'secret',
  'authorization',
  'cookie',
  'email',
  'phone',
  'otp',
  'ssn',
] as const;

let eventCount = 0;
let isLogging = false;
let trackingInitialized = false;

const monitoringSessionId = (() => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `web_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
})();

const truncate = (value: string, max: number) => (value.length > max ? `${value.slice(0, max)}...` : value);

const isSensitiveContextKey = (key: string) => {
  const lowered = key.toLowerCase();
  return SENSITIVE_CONTEXT_KEYWORDS.some((needle) => lowered.includes(needle));
};

const redactContextValue = (value: unknown, depth = 0): unknown => {
  if (value === null || value === undefined) return value;
  if (depth >= MAX_ROUTE_CONTEXT_DEPTH) return '[truncated-depth]';
  if (typeof value === 'string') return truncate(value, 180);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value)) {
    return value.slice(0, MAX_ROUTE_ARRAY_ITEMS).map((entry) => redactContextValue(entry, depth + 1));
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).slice(0, MAX_ROUTE_OBJECT_KEYS);
    const redacted: Record<string, unknown> = {};
    entries.forEach(([key, entryValue]) => {
      redacted[key] = isSensitiveContextKey(key) ? '[redacted]' : redactContextValue(entryValue, depth + 1);
    });
    return redacted;
  }
  return truncate(String(value), 180);
};

const getCurrentPageContext = () => {
  if (typeof window === 'undefined') {
    return {
      screenOrPage: 'unknown_page',
      routeParamsRedacted: null as Record<string, unknown> | null,
      pagePath: null as string | null,
    };
  }

  const queryContext: Record<string, unknown> = {};
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.forEach((value, key) => {
    queryContext[key] = isSensitiveContextKey(key) ? '[redacted]' : truncate(value, 180);
  });

  const pagePath = window.location.pathname || '/';
  return {
    screenOrPage: pagePath || 'unknown_page',
    routeParamsRedacted: Object.keys(queryContext).length ? queryContext : null,
    pagePath,
  };
};

const extractStructuredMetadata = (args: unknown[]) => {
  const metadataSource = args.find((arg) => {
    if (!arg || typeof arg !== 'object' || Array.isArray(arg)) return false;
    const obj = arg as Record<string, unknown>;
    return (
      typeof obj.actionName === 'string' ||
      typeof obj.functionName === 'string' ||
      typeof obj.edgeFunction === 'string' ||
      typeof obj.httpStatus === 'number' ||
      typeof obj.requestId === 'string' ||
      typeof obj.recoverable === 'boolean'
    );
  }) as Record<string, unknown> | undefined;

  return {
    actionName: typeof metadataSource?.actionName === 'string' ? metadataSource.actionName : undefined,
    functionName: typeof metadataSource?.functionName === 'string' ? metadataSource.functionName : undefined,
    edgeFunction: typeof metadataSource?.edgeFunction === 'string' ? metadataSource.edgeFunction : undefined,
    httpStatus: typeof metadataSource?.httpStatus === 'number' ? metadataSource.httpStatus : undefined,
    requestId: typeof metadataSource?.requestId === 'string' ? metadataSource.requestId : undefined,
    recoverable: typeof metadataSource?.recoverable === 'boolean' ? metadataSource.recoverable : undefined,
  };
};

const resolveRecoverable = (
  level: AppErrorLevel,
  source?: string,
  context?: Record<string, unknown>,
) => {
  if (typeof context?.recoverable === 'boolean') {
    return context.recoverable;
  }
  if (source === 'browser_extension') {
    return true;
  }
  if (source === 'window.onerror' || source === 'unhandledrejection') {
    return false;
  }
  return level !== 'error' ? true : true;
};

/**
 * Detect third-party browser extension failures that bubble through window.onerror
 * but are unrelated to app code (e.g. wallet extensions fighting over window.ethereum).
 */
const isBrowserExtensionNoise = (input: {
  message?: string | null;
  stack?: string | null;
  filename?: string | null;
  reason?: unknown;
}) => {
  const message = typeof input.message === 'string' ? input.message : '';
  const stack = typeof input.stack === 'string' ? input.stack : '';
  const filename = typeof input.filename === 'string' ? input.filename : '';
  const reasonText =
    input.reason instanceof Error
      ? `${input.reason.message}\n${input.reason.stack ?? ''}`
      : typeof input.reason === 'string'
        ? input.reason
        : '';

  const combined = `${message}\n${stack}\n${filename}\n${reasonText}`;
  if (!combined.trim()) return false;

  const lowered = combined.toLowerCase();
  const fromExtensionUrl =
    lowered.includes('chrome-extension://') ||
    lowered.includes('moz-extension://') ||
    lowered.includes('safari-web-extension://');

  // Phantom (evmAsk.js) / MetaMask / similar wallet injectors fighting over window.ethereum
  const ethereumRedefine =
    lowered.includes('cannot redefine property: ethereum') ||
    lowered.includes('cannot set property ethereum') ||
    (lowered.includes('cannot redefine property') && lowered.includes('ethereum'));

  const walletInjectorScript = /evmAsk\.js/i.test(combined) || lowered.includes('inpage.js');

  return (
    ethereumRedefine ||
    (fromExtensionUrl && lowered.includes('ethereum')) ||
    (walletInjectorScript && fromExtensionUrl)
  );
};

/**
 * Safari and some other browsers throw once history.replaceState is called
 * more than 100 times in 10 seconds. The page can keep working without the URL write.
 */
const isHistoryReplaceRateLimitError = (message?: string | null) => {
  if (!message) return false;
  const lowered = message.toLowerCase();
  return lowered.includes('replacestate') && lowered.includes('more than 100 times');
};

type GlobalErrorClassification = {
  suppressUnhandled: boolean;
  level: AppErrorLevel;
  source: string;
  recoverable: boolean;
  noiseCategory?: string;
  ignoredReason?: string;
};

const classifyGlobalRuntimeError = (input: {
  message?: string | null;
  stack?: string | null;
  filename?: string | null;
  reason?: unknown;
}): GlobalErrorClassification => {
  if (isBrowserExtensionNoise(input)) {
    return {
      suppressUnhandled: true,
      level: 'warning',
      source: 'browser_extension',
      recoverable: true,
      noiseCategory: 'browser_extension',
      ignoredReason:
        'Third-party browser extension conflict (e.g. wallet injector redefining window.ethereum); not app code',
    };
  }

  if (isHistoryReplaceRateLimitError(input.message)) {
    return {
      suppressUnhandled: true,
      level: 'warning',
      source: 'history.replaceState',
      recoverable: true,
      noiseCategory: 'history_replace_rate_limit',
      ignoredReason:
        'Browser blocked history.replaceState after too many calls in a short window; the page stays on the current URL',
    };
  }

  return {
    suppressUnhandled: false,
    level: 'error',
    source: 'window.onerror',
    recoverable: false,
  };
};

const safeSerialize = (value: unknown) => {
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }
  if (typeof value === 'string') {
    return truncate(value, MAX_CONTEXT_LENGTH);
  }
  if (typeof value === 'number' || typeof value === 'boolean' || value === null) {
    return value;
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    try {
      return truncate(String(value), MAX_CONTEXT_LENGTH);
    } catch {
      return '[unserializable]';
    }
  }
};

const buildMessage = (args: unknown[]) => {
  if (!args.length) return 'Unknown error';
  const message = args
    .map((arg) => {
      if (arg instanceof Error) return arg.message;
      if (typeof arg === 'string') return arg;
      try {
        return JSON.stringify(arg);
      } catch {
        return String(arg);
      }
    })
    .join(' ');
  return truncate(message, MAX_MESSAGE_LENGTH);
};

const extractStack = (args: unknown[]) => {
  const errorArg = args.find((arg) => arg instanceof Error) as Error | undefined;
  return errorArg?.stack;
};

const HIGH_SIGNAL_INFO_PATTERNS = [
  'network',
  'reconnect',
  'foreground',
  'session',
  'telemetry',
  'monitor',
  'queue',
] as const;

const shouldCaptureInfoDiagnostic = (message: string) => {
  const lowered = message.toLowerCase();
  return HIGH_SIGNAL_INFO_PATTERNS.some((token) => lowered.includes(token));
};

const sendErrorEvent = async (
  level: AppErrorLevel,
  message: string,
  options?: { stack?: string; source?: string; context?: Record<string, unknown>; tags?: Record<string, unknown> },
) => {
  if (eventCount >= MAX_EVENTS_PER_SESSION) return;
  if (isLogging) return;
  eventCount += 1;
  isLogging = true;
  try {
    const pageContext = getCurrentPageContext();
    const recoverable = resolveRecoverable(level, options?.source, options?.context);
    const mergedContext: Record<string, unknown> = {
      screenOrPage: pageContext.screenOrPage,
      pagePath: pageContext.pagePath,
      routeParamsRedacted: pageContext.routeParamsRedacted,
      recoverable,
      ...(options?.context ?? {}),
    };

    if (typeof mergedContext.responseBody !== 'undefined') {
      mergedContext.responseBody = redactContextValue(mergedContext.responseBody);
    }

    const mergedTags: Record<string, unknown> = {
      ...(options?.tags ?? {}),
      screenOrPage: pageContext.screenOrPage,
      severity: level,
      recoverable,
    };

    if (typeof mergedContext.actionName === 'string') {
      mergedTags.actionName = mergedContext.actionName;
    }
    if (typeof mergedContext.functionName === 'string') {
      mergedTags.functionName = mergedContext.functionName;
    }
    if (typeof mergedContext.edgeFunction === 'string') {
      mergedTags.edgeFunction = mergedContext.edgeFunction;
    }

    await appErrorMonitorService.logEvent({
      level,
      message,
      stack: options?.stack ?? null,
      source: options?.source ?? null,
      url: typeof window !== 'undefined' ? window.location.href : null,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      platform: 'web',
      app_version: null,
      session_id: monitoringSessionId,
      context: mergedContext,
      tags: mergedTags,
    });
  } catch {
    // swallow
  } finally {
    isLogging = false;
  }
};

const sendTelemetryDiagnostic = async (
  eventName: string,
  message: string,
  context?: Record<string, unknown>,
) => {
  void telemetryMonitorService.logTelemetryEvent({
    level: 'info',
    event_name: eventName,
    message,
    source: 'web.console.info',
    platform: 'web',
    app_version: null,
    url: typeof window !== 'undefined' ? window.location.href : null,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    session_id: monitoringSessionId,
    context: context ?? null,
    tags: { highSignal: true },
    payload: {
      args: context?.args ?? null,
    },
  });
};

export const initErrorTracking = () => {
  if (typeof window === 'undefined' || trackingInitialized) return;
  trackingInitialized = true;

  const originalWarn = console.warn;
  const originalError = console.error;
  const originalInfo = console.info;

  window.addEventListener('error', (event) => {
    try {
      const message =
        (typeof event.message === 'string' && event.message) ||
        (event.error instanceof Error ? event.error.message : undefined) ||
        'Unhandled error';
      const stack = event.error instanceof Error ? event.error.stack : undefined;
      const filename = typeof event.filename === 'string' ? event.filename : undefined;
      const classification = classifyGlobalRuntimeError({ message, stack, filename });

      if (classification.suppressUnhandled) {
        // Mark handled so the browser does not treat this as an uncaught app exception.
        event.preventDefault();
      }

      void sendErrorEvent(classification.level, message, {
        stack,
        source: classification.suppressUnhandled ? classification.source : 'window.onerror',
        context: {
          filename: filename ?? null,
          lineno: typeof event.lineno === 'number' ? event.lineno : null,
          colno: typeof event.colno === 'number' ? event.colno : null,
          recoverable: classification.recoverable,
          ...(classification.noiseCategory
            ? {
                noiseCategory: classification.noiseCategory,
                ignoredReason: classification.ignoredReason,
              }
            : {}),
        },
      });
    } catch (handlerFailure) {
      // Never let the global error handler itself throw.
      try {
        originalError.call(console, '[errorTracking] window.onerror handler failed', handlerFailure);
      } catch {
        // swallow
      }
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    try {
      const reason = event.reason;
      const message =
        reason instanceof Error
          ? reason.message
          : typeof reason === 'string'
            ? reason
            : 'Unhandled rejection';
      const stack = reason instanceof Error ? reason.stack : undefined;
      const classification = classifyGlobalRuntimeError({ message, stack, reason });

      if (classification.suppressUnhandled) {
        event.preventDefault();
      }

      void sendErrorEvent(classification.level, message, {
        stack,
        source: classification.suppressUnhandled ? classification.source : 'unhandledrejection',
        context: {
          reason: safeSerialize(reason),
          recoverable: classification.recoverable,
          ...(classification.noiseCategory
            ? {
                noiseCategory: classification.noiseCategory,
                ignoredReason: classification.ignoredReason,
              }
            : {}),
        },
      });
    } catch (handlerFailure) {
      try {
        originalError.call(console, '[errorTracking] unhandledrejection handler failed', handlerFailure);
      } catch {
        // swallow
      }
    }
  });

  console.warn = (...args: unknown[]) => {
    const message = buildMessage(args);
    const metadata = extractStructuredMetadata(args);
    void sendErrorEvent('warning', message, {
      stack: extractStack(args),
      source: 'console.warn',
      context: {
        args: args.map(safeSerialize),
        ...metadata,
      },
    });
    originalWarn(...args);
  };

  console.error = (...args: unknown[]) => {
    const message = buildMessage(args);
    const metadata = extractStructuredMetadata(args);
    void sendErrorEvent('error', message, {
      stack: extractStack(args),
      source: 'console.error',
      context: {
        args: args.map(safeSerialize),
        ...metadata,
      },
      tags: {
        actionName: metadata.actionName,
        functionName: metadata.functionName,
        edgeFunction: metadata.edgeFunction,
      },
    });
    originalError(...args);
  };

  console.info = (...args: unknown[]) => {
    const message = buildMessage(args);
    if (shouldCaptureInfoDiagnostic(message)) {
      const metadata = extractStructuredMetadata(args);
      void sendTelemetryDiagnostic('web-console-diagnostic', message, {
        args: args.map(safeSerialize),
        ...metadata,
        screenOrPage: getCurrentPageContext().screenOrPage,
      });
    }
    originalInfo(...args);
  };
};

export const __errorTrackingTestUtils = {
  redactContextValue,
  getCurrentPageContext,
  extractStructuredMetadata,
  resolveRecoverable,
  isBrowserExtensionNoise,
  isHistoryReplaceRateLimitError,
  classifyGlobalRuntimeError,
};
